/**
 * Onboarding wizard types — snake_case matching BFF API response.
 *
 * 3-step wizard: Company & Wallet → Contact Details → Review & Submit
 */

// ── Constants ───────────────────────────────────────────────────

export const CUSTODIAL_TYPES = ["Custodial", "Non-Custodial"] as const;

// Only Stellar is offered during onboarding for now; Solana ("SOL") and
// Polygon ("POL") are temporarily hidden until those chains are supported.
export const CHAINS = ["Stellar"] as const;

export const ASSETS = ["USDC", "USDT", "EURC"] as const;

export const WIZARD_STEPS = [
	{ id: 1, label: "Company & Wallet" },
	{ id: 2, label: "Contact Details" },
	{ id: 3, label: "Review & Submit" },
] as const;

// ── Form Data (what the wizard collects) ────────────────────────

export interface OnboardingContact {
	name: string;
	email: string;
	phone: string;
	role: string;
}

export interface OnboardingFormData {
	// Step 1: Company & Wallet
	company_name: string;
	wallet_name: string;
	custodial_type: string;
	chain: string;
	asset: string;
	products: string[];
	country_of_incorporation: string;
	website?: string;
	business_description: string;

	// Step 2: Contact Details
	primary_contact: OnboardingContact;
	secondary_contact?: Partial<OnboardingContact>;

	// Step 3: Review
	terms_accepted: boolean;
}

// ── Application Status ──────────────────────────────────────────

export type ApplicationStatus =
	| "draft"
	| "submitted"
	| "under_review"
	| "approved"
	| "rejected"
	| "approval_failed";

export const STATUS_LABELS: Record<ApplicationStatus, string> = {
	draft: "Draft",
	submitted: "Submitted",
	under_review: "Under Review",
	approved: "Approved",
	rejected: "Rejected",
	approval_failed: "Approval Failed",
};

export const STATUS_COLORS: Record<ApplicationStatus, string> = {
	draft: "var(--color-text-secondary)",
	submitted: "var(--color-primary)",
	under_review: "var(--color-warning)",
	approved: "var(--color-success)",
	rejected: "var(--color-error-500)",
	approval_failed: "var(--color-error-500)",
};

// ── Application (full record from BFF, extends form data) ───────

export interface Application extends OnboardingFormData {
	id: string;
	status: ApplicationStatus;
	current_step: number;
	created_at: string;
	updated_at: string;
	submitted_at?: string;
	ip_address?: string;
	captcha_verified?: boolean;
	application_token?: string;

	// Approval fields
	approved_wallet_id?: string;
	approved_user_id?: string;
	reviewed_by?: string;
	reviewed_at?: string;
	reviewer_notes?: string;
	approval_error?: string;
	notification_status?: string;

	// Extended fields from BFF (may not always be present)
	company_legal_name?: string;
	company_dba?: string;
	entity_type?: string;
	registration_number?: string;
	registered_address?: {
		street?: string;
		city?: string;
		state?: string;
		postalCode?: string;
		country?: string;
	};
	estimated_mau_range?: string;
	conducts_kyc?: string;
	is_regulated?: string;
	licenses?: string[];
	jurisdictions?: string[];
	compliance_officer?: { name: string; email: string };
	documents?: { type: string; s3Key: string; fileName: string }[];
}
